import React from 'react';
import styled from 'styled-components';
import * as remoteApi from 'api/remote';
import theme from 'theme';
import { useTranslation } from 'react-i18next';
import { DragHandle, WidgetContainer, WidgetContent, WidgetHeader } from '.';
import { DashboardWidget, Workspace } from 'types';
import KoalaIcon from 'koala/components/Icons';
import { useQuery } from 'react-query';
import queryKeys from 'config/queryKeys';
import { shallowEqual, useSelector } from 'react-redux';
import KoalaLoader from 'koala/components/Loader';
import KoalaConfidencePie from 'koala/components/ConfidencePie';
import { CustomTermKey, translate } from 'utils/customTermUtils';
import KoalaColorBlock from 'koala/components/ColorBlock';
import * as initiativeUtils from 'utils/initiativeUtils';
import { InitiativeStates } from 'utils/initiativeUtils';
import { VictoryTooltip } from 'victory';

const Container = styled.div`
  height: 18rem;
  display: flex;
  width: 100%;
  height: 90%;
  max-height: 100%;
  gap: ${theme.spacing.x2};
  align-items: center;
  gap: ${theme.spacing.x4};
  height: 100%;

  @media ${theme.devices.mobile} {
    max-height: 25rem;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  row-gap: ${theme.spacing.x3};
  column-gap: ${theme.spacing.x3};
  height: 100%;
  justify-content: center;
`;

const LegendItem = styled.div`
  display: flex;
  gap: ${theme.spacing.x1};
`;

interface ProgressData {
  x: number;
  y: number;
  color: string;
  legendTitle?: string;
  label?: string;
}

interface Props {
  widget: DashboardWidget;
}

function InitiativeStatusPieWidget(props: Props) {
  const { widget } = props;
  const currentWorkspace: Workspace = useSelector((state: any) => state.session.currentWorkspace, shallowEqual);
  const { t } = useTranslation();

  const initiativesFilterParams = {
    archived: false,
    is_in_progress: true,
  };

  const queryKeyInitiativesStats = [
    queryKeys.initiatives,
    'stats',
    {
      workspaceSlug: currentWorkspace.slug,
      filter: initiativesFilterParams,
    },
  ];
  const { data: initiativesStatsData, isLoading } = useQuery(
    queryKeyInitiativesStats,
    remoteApi.fetchInitiativesStats,
    {
      staleTime: 0,
    },
  );

  const widgetTitle: string = widget.title
    ? widget.title
    : t('workspaceDashboards.status-pie-chart', {
        initiative: translate(currentWorkspace, CustomTermKey.INITIATIVE, 1),
      });

  const data = initiativesStatsData?.data;

  if (!data) {
    return (
      <WidgetContainer>
        <WidgetHeader>
          <DragHandle className="drag-handle">
            <KoalaIcon iconName="grab" iconSize="small" />
          </DragHandle>
          <p className="widget-type">{widgetTitle}</p>
        </WidgetHeader>
        <WidgetContent>
          <p>{t('workspaceDashboards.widgetError')}</p>
        </WidgetContent>
      </WidgetContainer>
    );
  }

  const progressData: ProgressData[] = [];

  const getWorkStateColorString = (state: string) => {
    switch (state) {
      case InitiativeStates.BACKLOG:
        return theme.colors.N20;
      case InitiativeStates.PLANNED:
        return theme.colors.Y30;
      case InitiativeStates.DESIGN:
        return theme.colors.B20;
      case InitiativeStates.IN_PROGRESS:
        return theme.colors.B50;
      case InitiativeStates.IN_REVIEW:
        return theme.colors.B80;
      case InitiativeStates.READY:
        return theme.colors.T50;
      case InitiativeStates.BLOCKED:
        return theme.colors.R20;
      case InitiativeStates.DONE:
        return theme.colors.G20;
      case InitiativeStates.ABANDONED:
        return theme.colors.N80;
      default:
        return theme.colors.N50;
    }
  };

  Object.keys(data).forEach((key: string, index: number) => {
    const state = key.includes('_initiatives_count') ? key.split('_initiatives_count')[0] : key;
    if (!Object.values(InitiativeStates).includes(state as InitiativeStates)) {
      return;
    }

    const label = initiativeUtils.getWorkState(state, currentWorkspace, t);
    const color = getWorkStateColorString(state);
    progressData.push({
      x: index,
      y: data[key],
      color,
      label: `${data[key]} ${label}`,
    });
  });

  const labelComponent = (
    <VictoryTooltip
      orientation="right"
      flyoutPadding={{ top: 4, bottom: 4, left: 8, right: 8 }}
      cornerRadius={4}
      flyoutStyle={{
        fill: theme.colors.N5,
        stroke: theme.colors.N5,
      }}
      style={[
        {
          fontSize: '12px',
          fontFamily: theme.font.fontFamily,
        },
      ]}
    />
  );

  return (
    <WidgetContainer>
      <WidgetHeader>
        <DragHandle className="drag-handle">
          <KoalaIcon iconName="grab" iconSize="small" />
        </DragHandle>
        <p className="widget-type">{widgetTitle}</p>
      </WidgetHeader>
      <WidgetContent>
        {isLoading && <KoalaLoader />}
        <Container className="panel-checkins-chart">
          <KoalaConfidencePie data={progressData} size="large" tooltipComponent={labelComponent} />
          <LegendContainer>
            {progressData.map((data) => {
              return (
                <LegendItem>
                  <KoalaColorBlock color={data.color} />
                  {data.label}
                </LegendItem>
              );
            })}
          </LegendContainer>
        </Container>
      </WidgetContent>
    </WidgetContainer>
  );
}

export default InitiativeStatusPieWidget;
